import { PagesProvider, getPageInOrder } from '@quno/quno-gen';
import { Fragment, useEffect, useState } from 'react';
import { MetaTags } from '@quno/patient-journey/src/components/shared/MetaTags/MetaTags';
import renderBlock from '@quno/patient-journey/src/contentful/patient-journey/renderBlocks';
import { Header } from '@quno/patient-journey/src/components/Header/Header';
import { Footer } from '@quno/patient-journey/src/components/Footer/Footer';
import { Layout } from '@quno/patient-journey/src/components/shared/Layout/Layout';
import { ModalContext } from '@quno/patient-journey/src/context/ModalContext';
import { Modal } from '@quno/patient-journey/src/components/shared/Modal';
import { useRouter } from 'next/router';
import { findLeadOrOpportunity } from '@quno/patient-journey/src/data/fetch/lead-or-opportunity';
import { PatientContext } from '@quno/patient-journey/src/context/PatientContext';
import { ExistingQuoteModal } from '@quno/patient-journey/src/components/shared/Modals/ExistingQuoteModal';
import {
  defaultLocale,
  localeMapper,
  type FormLocales,
} from '@quno/onboarding/src/constants/locales';
import { useTranslation } from '@quno/patient-journey/src/hooks/useTranslation';
import clsx from 'clsx';
import styles from './Page.module.scss';
import type { Model } from '@quno/onboarding/src/reports/generator/types/models';
import type { PatientContextProps } from '@quno/patient-journey/src/context/PatientContext';
import type { Brand } from '@quno/patient-journey/src/contentful/generated/graphql';
import type { ModalContextProps } from '@quno/patient-journey/src/context/ModalContext';
import type { Page as PageFragment } from '@quno/patient-journey/src/contentful/generated/graphql-patient-journey';

type PageProps = PageFragment & {
  isPreview?: boolean;
  locale: FormLocales;
  useMasterDetailConnectivity?: {
    onClick?: (id?: string) => void;
    onMouseEnter?: (id?: string) => void;
    onMouseLeave?: (id?: string) => void;
    masterHoveredId?: string;
  };
  selectedBlockId?: string;
};

export const Page = (props: PageProps): JSX.Element => {
  const { locale: pageLocale } = useTranslation(true);
  const locale = props.locale || localeMapper[pageLocale] || defaultLocale;
  const pages = getPageInOrder(props as object) as PageProps[];
  const page = pages[0] as PageProps;
  const brand = page.brand as unknown as Brand;
  const [modal, setModal] = useState<ModalContextProps | null>(null);

  const [patient, setPatient] = useState<PatientContextProps | null>(null);

  const router = useRouter();

  const junoId = router.query?.junoid;

  useEffect(() => {
    const run = async (): Promise<void> => {
      const leadOrOpportunityExists = await findLeadOrOpportunity(
        junoId as string,
      );

      if (!leadOrOpportunityExists) {
        return;
      }

      if (leadOrOpportunityExists.quotes?.length) {
        setModal({
          isOpen: true,
          showCloseButton: false,
          showPrevIcon: false,
          content: (
            <ExistingQuoteModal
              quote={leadOrOpportunityExists.quotes?.[0]}
              junoId={junoId as string}
            />
          ),
          onModalClose() {
            void router.replace(
              `/${router.locale}/${router.asPath.split('?')[0]}`,
            );
          },
        });

        return;
      }

      setPatient({
        junoId: junoId as string,
        firstName: leadOrOpportunityExists?.data.firstName || '',
        lastName: leadOrOpportunityExists?.data.lastName || '',
      });
    };

    if (junoId && brand.settings.allowJunoId) {
      void run();
    }
  }, [junoId]);

  const {
    blocksCollection,
    seoTitle,
    seoDescription,
    onboardingJson,
    isPreview,
    useMasterDetailConnectivity,
    selectedBlockId,
  } = props;

  const items = isPreview
    ? blocksCollection?.items || []
    : onboardingJson?.attributes?.pageBlocks?.[defaultLocale] || [];

  const content = items.map(
    (block: { onboardingJson?: Model; uuid: string }, i: number) => {
      if (!useMasterDetailConnectivity) {
        return (
          <Fragment key={block.uuid}>
            {renderBlock(block, {
              blocks: items || [],
            })}
          </Fragment>
        );
      }

      return (
        <div
          key={block.uuid}
          id={block?.uuid}
          style={{ position: 'relative' }}
          onClick={() => useMasterDetailConnectivity?.onClick?.(block?.uuid)}
          onMouseEnter={() =>
            useMasterDetailConnectivity?.onMouseEnter?.(block?.uuid)
          }
          onMouseLeave={() =>
            useMasterDetailConnectivity?.onMouseLeave?.(block?.uuid)
          }
          className={clsx(
            block?.uuid !== selectedBlockId && styles.highlightOnHover,
            useMasterDetailConnectivity?.masterHoveredId === block?.uuid &&
              styles.highlighted,
          )}
        >
          {renderBlock(block, {
            blocks: items || [],
          })}
        </div>
      );
    },
  );

  const header = <Header />;

  const jsonAttributes = props?.onboardingJson?.attributes || {};
  const footerAddress = jsonAttributes.footer?.footerAddress;
  const footerContact = jsonAttributes.footer?.footerContact;
  const footerBackgroundColor =
    jsonAttributes.footer?.footerBackgroundColor?.[defaultLocale];
  const footerBackgroundColorOpacity =
    jsonAttributes.footer?.footerBackgroundColorOpacity?.[defaultLocale];

  const footerFontColor =
    jsonAttributes.footer?.footerFontColor?.[defaultLocale];

  const hasFooter = footerAddress?.[locale] || footerContact?.[locale];

  const footer = hasFooter ? (
    <Footer
      footerAddress={footerAddress}
      footerContact={footerContact}
      backgroundColorOverwrite={footerBackgroundColor}
      fontColorOverride={footerFontColor}
      backgroundColorOpacity={footerBackgroundColorOpacity}
      locale={locale}
    />
  ) : undefined;

  return (
    <PagesProvider pages={pages}>
      <MetaTags
        title={seoTitle}
        description={seoDescription}
        ogImage={brand?.ogImage?.url}
        ogDescription={seoDescription}
        favicon={brand?.favicon?.url}
      />

      <Layout header={header} footer={footer}>
        <ModalContext.Provider value={{ modal, setModal }}>
          <PatientContext.Provider value={{ patient, setPatient }}>
            {content}
            <Modal />
          </PatientContext.Provider>
        </ModalContext.Provider>
      </Layout>
    </PagesProvider>
  );
};
